import { resolveImageAltText } from '@/features/content/imageAltText';
import {
  getContentUnitAnchorId,
  type NormalizedContentUnit,
} from '@/features/content/useComponent';

import type { EditableContentUnit } from './editorTypes';

function normalizeString(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function getSummaryLabel(
  heading: string | undefined,
  numberedHeading: string | undefined,
  unitPosition: number,
) {
  return numberedHeading ?? heading ?? `Content item ${unitPosition}`;
}

function isExampleHeading(heading: string | undefined) {
  return /^example\b/i.test(heading?.trim() ?? '');
}

function shouldNumberHeading(unit: EditableContentUnit) {
  return Boolean(
    unit.heading &&
      !unit.collapseByDefault &&
      !unit.disableAutoNumbering &&
      !isExampleHeading(unit.heading),
  );
}

function createBaseDisplayUnit(
  unit: EditableContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
) {
  const heading = normalizeString(unit.heading);
  const caption = normalizeString(unit.caption);
  const numberedHeading = heading
    ? shouldNumberHeading(unit)
      ? `${nextHeadingNumber()}. ${heading}`
      : heading
    : undefined;

  return {
    caption,
    collapseByDefault: unit.collapseByDefault,
    disableAutoNumbering: unit.disableAutoNumbering,
    heading,
    // The editor row owns the anchor to avoid duplicate IDs with the wrapper.
    anchorId: undefined,
    id: unit.id,
    numberedHeading,
    summaryLabel: getSummaryLabel(heading, numberedHeading, unitPosition),
  };
}

function getImageSize(unit: EditableContentUnit) {
  if (unit.imageSize === 'SMALL') {
    return 'small' as const;
  }

  if (unit.imageSize === 'LARGE') {
    return 'large' as const;
  }

  return 'medium' as const;
}

function getTextValue(unit: EditableContentUnit) {
  if (unit.editorType === 'EQUATION') {
    const latex = unit.latex?.trim();
    return latex ? `$$${latex}$$` : '';
  }

  return unit.text ?? unit.originalText ?? '';
}

export function editableContentUnitToDisplayUnit(
  unit: EditableContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
): NormalizedContentUnit | null {
  const baseUnit = createBaseDisplayUnit(unit, unitPosition, nextHeadingNumber);

  if (unit.editorType === 'TEXT' || unit.editorType === 'EQUATION') {
    const html = getTextValue(unit);

    if (!html.trim() || html.trim() === '<p></p>') {
      return null;
    }

    return {
      ...baseUnit,
      html,
      kind: 'text',
    };
  }

  if (unit.editorType === 'IMAGE') {
    const src = normalizeString(unit.dynamicImageUrl) ?? normalizeString(unit.imageUrl);

    if (!src) {
      return null;
    }

    return {
      ...baseUnit,
      // Named as the student view names it: its alt text, else a legacy
      // caption's marker, else nothing, and so decorative. The heading and the
      // caption that used to stand in are on screen beside it already, and
      // say nothing of what the image shows.
      alt: resolveImageAltText({ altText: unit.altText, caption: unit.caption }),
      kind: 'image',
      size: getImageSize(unit),
      src,
    };
  }

  if (unit.editorType === 'TABLE') {
    const html = unit.tableHtml?.trim();

    if (!html) {
      return null;
    }

    return {
      ...baseUnit,
      density: unit.tableDensity ?? 'comfortable',
      headerRow: unit.tableHeaderRow ?? true,
      html,
      kind: 'table',
      stickyHeader: unit.tableStickyHeader ?? false,
      style: unit.tableStyle ?? 'plain',
    };
  }

  if (unit.editorType === 'IFRAME') {
    const src = normalizeString(unit.iframeSource);

    if (!src) {
      return null;
    }

    return {
      ...baseUnit,
      height: unit.iframeHeight ?? 420,
      kind: 'iframe',
      src,
      title: baseUnit.heading ?? baseUnit.caption ?? 'Embedded activity',
      widthPercent: Math.min(100, Math.max(10, unit.iframeWidth ?? 100)),
    };
  }

  const videoId = normalizeString(unit.videoId);

  if (!videoId) {
    return null;
  }

  return {
    ...baseUnit,
    embedUrl: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`,
    kind: 'inlineVideo',
    title: baseUnit.heading ?? baseUnit.caption ?? 'Embedded video',
    videoId,
  };
}

export function createDisplayUnitsForEditableContent(
  units: EditableContentUnit[],
) {
  let headingNumber = 0;

  return units.map((unit, index) =>
    editableContentUnitToDisplayUnit(unit, index + 1, () => {
      headingNumber += 1;
      return headingNumber;
    }),
  );
}

export function getEditableContentUnitAnchorId(unitId: string) {
  return getContentUnitAnchorId(unitId);
}
